import {
  Anchor,
  Center,
  Flex,
  Group,
  Modal,
  Stack,
  Text,
  Title,
  Tooltip,
  useMantineColorScheme,
  useMatches,
} from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import dayjs from "dayjs";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AuctionsChartTooltip } from "./components";
import { ChartFilterDropdown } from "./components/ChartFilterDropdown/ChartFilterDropdown";
import { ChartFiltersModal } from "./components/ChartFiltersModal/ChartFiltersModal";
import { ChartFiltersPopover } from "./components/ChartFiltersPopover/ChartFiltersPopover";
import { ChartHoverTooltip } from "./components/ChartHoverTooltip/ChartHoverTooltip";
import { ChartLegend } from "./components/ChartLegend/ChartLegend";
import { ChartPoint } from "./components/ChartPoint/ChartPoint";
import { filterChartData, filterChartPayloadByDateRange, normalizeData } from "./utils";

import {
  EmptyStateRevamped,
  ScatterAndLineChart,
  TimeframeInput,
  TrendlineType,
} from "@/components/elements";
import { IconInfo } from "@/components/icons";
import { defaultCurrency } from "@/lib/currency";
import { Currency } from "@/types/currency";
import { formatEuro } from "@/utils/formatEuro";
import { getChartPointColor, getChartPointShape } from "@/utils/transactionRecord";

import type { TimeframeRange } from "@/components/elements/TimeframeInput/types";
import type { AuctionCarTransaction, AuctionsChartPayload } from "@/types/auctions";
import type { DatesRange } from "@/types/entity";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type AuctionsChartProps = {
  availableConditions?: AuctionCarTransaction["condition"][];
  availableResults?: AuctionCarTransaction["result"][];
  carId: string;
  data?: AuctionsChartPayload[];
  dateRange?: DatesRange;
  hasActiveFilters?: boolean;
  /** When false, legend and filter controls are hidden. */
  hasSourceData?: boolean;
  onCancelAllFilters?: () => void;
  onConditionsChange?: (values: string[]) => void;
  onPointClick?: (transactionId: string) => void;
  onResultsChange?: (values: string[]) => void;
  onShowOutliersChange?: (value: boolean) => void;
  onShowReplicasChange?: (value: boolean) => void;
  onTimeframeChange?: (range?: DatesRange) => void;
  selectedConditions?: string[];
  selectedResults?: string[];
  showOutliers?: boolean;
  showReplicas?: boolean;
  suppressEmptyState?: boolean;
  trendline?: TrendlineType;
  /** When false, the trend line is not rendered. Defaults to false. */
  withTrendline?: boolean;
  zeroPriceLabel?: "poa" | "not-available";
  isEmptyState?: boolean;
  hideTitle?: boolean;
} & MantineStyleProps;

const LARGE_DATASET_THRESHOLD = 10;

/** Deterministic Y-axis jitter in pixels; range is ±(range/2). */
function deterministicJitter(index: number, range = 8): number {
  const x = Math.sin(index * 9301 + 49297) * 233280;
  return (x - Math.floor(x) - 0.5) * range;
}

export const AuctionsChart: FC<AuctionsChartProps> = ({
  availableConditions = [],
  availableResults = [],
  carId,
  data,
  dateRange,
  hasActiveFilters = false,
  hasSourceData,
  onCancelAllFilters,
  onConditionsChange,
  onPointClick,
  onResultsChange,
  onShowOutliersChange,
  onShowReplicasChange,
  onTimeframeChange,
  selectedConditions = [],
  selectedResults = [],
  showOutliers = true,
  showReplicas = true,
  suppressEmptyState = false,
  trendline = "linear-trend",
  withTrendline = false,
  zeroPriceLabel = "not-available",
  isEmptyState = false,
  hideTitle = false,
  ...props
}) => {
  const { t } = useTranslation();
  const isMobile = useMatches({ base: true, md: false });
  const items = useMemo(() => {
    const dateFiltered = filterChartPayloadByDateRange(data || [], dateRange);
    return filterChartData(normalizeData(dateFiltered), { showOutliers, showReplicas });
  }, [data, dateRange, showOutliers, showReplicas]);

  const domain = useMemo<[number, number] | undefined>(() => {
    if (items.length === 0) return undefined;
    const dates = items.map((item) => item.date as number);
    return [Math.min(...dates), Math.max(...dates)];
  }, [items]);
  const hasItems = items.length > 0;
  const movingAverageRange = withTrendline && domain ? (domain[1] - domain[0]) / 20 : 0;

  const isLargeDataset = items.length >= LARGE_DATASET_THRESHOLD;

  const valueStats = useMemo(() => {
    const values = items.map((d) => d.value as number).filter((v) => v != null && isFinite(v));
    if (values.length === 0) return { min: 0, max: 1 };
    const min = Math.min(...values);
    const max = Math.max(...values);
    return { min, max: max === min ? min + 1 : max };
  }, [items]);

  const scatterMetaByIndex = useMemo(() => {
    return new Map(
      items.map((item, i) => {
        const value = item.value as number | null;
        const normalized =
          value != null && isFinite(value)
            ? (value - valueStats.min) / (valueStats.max - valueStats.min)
            : 0.5;
        const opacity = isLargeDataset ? 0.35 + normalized * 0.65 : 0.75;
        const jitter = isLargeDataset ? deterministicJitter(i) : 0;

        return [i, { opacity, jitter, color: getChartPointColor(item) }] as const;
      })
    );
  }, [items, valueStats, isLargeDataset]);

  const [isTimeframeTouched, setIsTimeframeTouched] = useState(false);
  const [timeframeInputKey, setTimeframeInputKey] = useState(0);
  const [hoveredPoint, setHoveredPoint] = useState<{
    index: number;
    left: number;
    top: number;
  } | null>(null);
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const hasSourceDataResolved = hasSourceData ?? (data?.length ?? 0) > 0;
  const showChartControls = hasSourceDataResolved;
  const hasTimeframe =
    showChartControls &&
    !!onTimeframeChange &&
    (hasItems || isTimeframeTouched || hasActiveFilters);
  const hasVisibilityFilters = !!(onShowOutliersChange && onShowReplicasChange);
  const hasAttributeFilters =
    showChartControls && (onConditionsChange || onResultsChange || hasVisibilityFilters);
  const showFiltersRow = hasTimeframe || hasAttributeFilters;
  const [legendOpened, setLegendOpened] = useState(false);

  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const chartColor = isDark ? "#80c9df" : "primary";
  const hoverColor = isDark ? "#2B4EB9" : "#143AB1";

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const conditionOptions = useMemo(
    () =>
      availableConditions.map((condition) => ({
        value: condition,
        label: t(`car.conditions.${condition}`, condition),
      })),
    [availableConditions, t]
  );

  const resultOptions = useMemo(
    () =>
      availableResults.map((result) => ({
        value: result,
        label: t(`components.TransactionTableRow.result.${result}`, result),
      })),
    [availableResults, t]
  );

  const handleCancelFilters = useCallback(() => {
    if (onCancelAllFilters) {
      onCancelAllFilters();
    } else {
      if (hasVisibilityFilters) {
        onShowReplicasChange?.(true);
        onShowOutliersChange?.(true);
      }
      onConditionsChange?.([]);
      onResultsChange?.([]);
      onTimeframeChange?.();
    }
    setIsTimeframeTouched(false);
    setTimeframeInputKey((current) => current + 1);
  }, [
    hasVisibilityFilters,
    onCancelAllFilters,
    onConditionsChange,
    onResultsChange,
    onShowOutliersChange,
    onShowReplicasChange,
    onTimeframeChange,
  ]);

  const handleTimeframeChange = useCallback(
    (dates?: TimeframeRange) => {
      if (dates?.[0] && dates?.[1]) {
        onTimeframeChange?.({ from: dates[0] as string, to: dates[1] as string });
        setIsTimeframeTouched(true);
      } else {
        onTimeframeChange?.();
        setIsTimeframeTouched(false);
      }
    },
    [onTimeframeChange]
  );

  const valueFormatter = useMemo(
    () => ({
      x: (value: number | undefined) => dayjs.unix(value ?? 0).format("MMM YYYY"),
      y: (value: number | undefined) =>
        formatEuro(value ?? 0, {
          notation: "compact",
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }),
    }),
    [currency.code]
  );

  const tooltipPayload = useMemo(() => {
    if (hoveredPoint == null) return undefined;
    const item = items[hoveredPoint.index];
    if (!item) return undefined;
    return [{ name: "value", value: item.value, payload: item }];
  }, [hoveredPoint, items]);

  const handlePointClick = useCallback(
    (index: number) => {
      const item = items[index];
      if (!item?.id || !onPointClick) return;
      onPointClick(item.id);
    },
    [items, onPointClick]
  );

  const scatterProps = useMemo(
    () => ({
      color: "yellow",
      shape: (shapeProps: any) => {
        const cx: number = shapeProps?.cx ?? 0;
        const cy: number = shapeProps?.cy ?? 0;
        const payload = shapeProps?.payload ?? {};
        const index: number = shapeProps?.index ?? 0;
        const meta = scatterMetaByIndex.get(index) ?? {
          opacity: 0.75,
          jitter: 0,
          color: getChartPointColor(payload),
        };
        const adjustedCy = cy + meta.jitter;
        const isHovered = hoveredPoint?.index === index;
        const fillColor = getChartPointColor(payload);
        const pointShape = getChartPointShape(payload);

        return (
          <ChartPoint
            cx={cx}
            cy={adjustedCy}
            fill={isHovered ? hoverColor : fillColor}
            fillOpacity={isHovered ? 1 : meta.opacity}
            shape={pointShape}
            onMouseEnter={(event) => {
              const container = chartContainerRef.current;
              if (!container) return;
              const targetRect = event.currentTarget.getBoundingClientRect();
              const containerRect = container.getBoundingClientRect();
              setHoveredPoint({
                index,
                left: targetRect.left - containerRect.left + targetRect.width / 2,
                top: targetRect.top - containerRect.top,
              });
            }}
            onMouseLeave={() => {
              setHoveredPoint(null);
            }}
            onClick={() => handlePointClick(index)}
            onMouseDown={(event) => event.preventDefault()}
            style={{ cursor: onPointClick ? "pointer" : "default", pointerEvents: "auto" }}
          />
        );
      },
    }),
    [handlePointClick, hoverColor, hoveredPoint, onPointClick, scatterMetaByIndex]
  );

  const lineProps = useMemo(() => ({ color: chartColor, type: "monotone" as const }), [chartColor]);
  const trendlineOptions = useMemo(
    () => ({ constrainToZero: true, movingAverageRange }),
    [movingAverageRange]
  );

  const legendTrigger = isMobile ? (
    <>
      <Flex
        align="center"
        gap="xs"
        wrap="nowrap"
        style={{ cursor: "pointer" }}
        onClick={() => setLegendOpened(true)}
      >
        <Text
          fz="sm"
          c="primary"
          style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
        >
          {t("components.AuctionsChart.check_legend")}
          <IconInfo size={18} color="primary" />
        </Text>
      </Flex>

      <Modal
        opened={legendOpened}
        onClose={() => setLegendOpened(false)}
        title={t("components.AuctionsChart.check_legend")}
        centered
        fullScreen={false}
        styles={{
          content: {
            backgroundColor: "#2B2B2B",
          },
          header: {
            backgroundColor: "#2B2B2B",
          },
          title: {
            color: "#E6E6E6",
          },
          close: {
            color: "#E6E6E6",
            backgroundColor: "transparent",
          },
          body: {
            color: "#E6E6E6",
          },
        }}
      >
        <ChartLegend />
      </Modal>
    </>
  ) : (
    <Tooltip
      label={<ChartLegend />}
      multiline
      maw={400}
      withArrow
      styles={{
        tooltip: {
          backgroundColor: isDark ? "#2B2B2B" : undefined,
          color: isDark ? "#E6E6E6" : undefined,
          border: isDark ? "1px solid #2B2B2B" : undefined,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.35)",
        },
        arrow: {
          backgroundColor: isDark ? "#2B2B2B" : undefined,
          border: isDark ? "1px solid #2B2B2B" : undefined,
        },
      }}
    >
      <Flex align="center" gap="xs" wrap="nowrap" style={{ cursor: "default" }}>
        <Text
          fz="sm"
          c="primary"
          style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
        >
          {t("components.AuctionsChart.check_legend")}
          <IconInfo size={18} color="primary" />
        </Text>
      </Flex>
    </Tooltip>
  );

  return (
    <Stack
      gap="lg"
      {...props}
      style={{
        filter: isEmptyState ? "grayscale(1)" : undefined,
        opacity: isEmptyState ? (isDark ? 0.4 : 0.7) : undefined,
        pointerEvents: isEmptyState ? "none" : undefined,
      }}
    >
      <Group align="flex-start" justify="space-between" wrap="nowrap" w="100%">
        {hideTitle ? null : isMobile ? (
          <Stack gap="md" style={{ flex: 1, minWidth: 0 }}>
            <Title order={3}>{t("components.AuctionAnalysis.title")}</Title>
            {showChartControls && legendTrigger}
          </Stack>
        ) : (
          <Group align="center" gap="lg" style={{ flex: 1, minWidth: 0 }} wrap="wrap">
            <Title
              order={3}
              opacity={!hasSourceDataResolved && !hasActiveFilters ? 0.3 : undefined}
            >
              {t("components.AuctionAnalysis.title")}
            </Title>
            {showChartControls && legendTrigger}
          </Group>
        )}
        {isMobile && showFiltersRow && showChartControls && (
          <ChartFiltersModal
            conditionOptions={conditionOptions}
            dateRange={dateRange}
            hasActiveFilters={hasActiveFilters}
            onCancelAllFilters={handleCancelFilters}
            onConditionsChange={onConditionsChange}
            onResultsChange={onResultsChange}
            onShowOutliersChange={onShowOutliersChange}
            onShowReplicasChange={onShowReplicasChange}
            onTimeframeChange={onTimeframeChange}
            onTimeframeTouchedChange={setIsTimeframeTouched}
            resultOptions={resultOptions}
            selectedConditions={selectedConditions}
            selectedResults={selectedResults}
            showOutliers={showOutliers}
            showReplicas={showReplicas}
          />
        )}
      </Group>

      {showFiltersRow && !isMobile && showChartControls && (
        <Group align="flex-end" gap="md" justify="space-between" wrap="wrap">
          {hasTimeframe && (
            <TimeframeInput
              key={timeframeInputKey}
              flex={1}
              label={t("components.PerformanceChart.timeframe")}
              defaultValue="all"
              onChange={handleTimeframeChange}
              inputProps={{
                placeholder: t("components.PerformanceChart.timeframe_placeholder"),
                miw: 200,
              }}
              maxDate={new Date()}
              presets={["all", "ytd", "1y", "3y", "5y"]}
              type="month"
              valueFormat="MMM YYYY"
            />
          )}

          {(hasActiveFilters || hasAttributeFilters) && (
            <Group align="center" gap="sm" wrap="nowrap">
              {hasActiveFilters && (
                <Anchor
                  c="danger"
                  component="button"
                  fz="sm"
                  onClick={handleCancelFilters}
                  style={{ flexShrink: 0, whiteSpace: "nowrap" }}
                  type="button"
                >
                  {t("components.AuctionsChart.cancel_filters")}
                </Anchor>
              )}
              {onConditionsChange && conditionOptions.length > 0 && (
                <ChartFilterDropdown
                  data={conditionOptions}
                  label={t("components.AuctionsChart.conditions_filter")}
                  onChange={onConditionsChange}
                  values={selectedConditions}
                />
              )}
              {onResultsChange && resultOptions.length > 0 && (
                <ChartFilterDropdown
                  data={resultOptions}
                  label={t("components.AuctionsChart.result_filter")}
                  onChange={onResultsChange}
                  values={selectedResults}
                />
              )}
              {hasVisibilityFilters && (
                <ChartFiltersPopover
                  onShowOutliersChange={onShowOutliersChange}
                  onShowReplicasChange={onShowReplicasChange}
                  showOutliers={showOutliers}
                  showReplicas={showReplicas}
                />
              )}
            </Group>
          )}
        </Group>
      )}

      {hasItems ? (
        <div
          ref={chartContainerRef}
          style={{ minWidth: 0, overflow: "visible", position: "relative" }}
        >
          <ScatterAndLineChart
            data={items}
            dataKey={{ x: "date", y: "value" }}
            h={300}
            legendProps={{ verticalAlign: "bottom", height: 50 }}
            lineLabel={withTrendline ? t(`components.AuctionsChart.${trendline}`) : undefined}
            lineProps={lineProps}
            scatterProps={scatterProps}
            strokeDasharray="0"
            xAxisProps={domain ? { domain } : undefined}
            valueFormatter={valueFormatter}
            trendline={trendline}
            trendlineOptions={trendlineOptions}
            withLegend={withTrendline}
            withTooltip={false}
            withTrendline={withTrendline}
          />
          {tooltipPayload && hoveredPoint && (
            <ChartHoverTooltip anchor={hoveredPoint} containerRef={chartContainerRef}>
              <AuctionsChartTooltip payload={tooltipPayload} zeroPriceLabel={zeroPriceLabel} />
            </ChartHoverTooltip>
          )}
        </div>
      ) : suppressEmptyState || !hasSourceDataResolved ? null : (
        <Center h="18em">
          <EmptyStateRevamped />
        </Center>
      )}
    </Stack>
  );
};
